<script setup lang="ts">
import type { CuratedEvent } from '~/types/curated-events';

type Props = {
  events: CuratedEvent[];
};

type Emits = {
  (e: 'date-change', date: string): void;
};

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const grid = ref<HTMLElement | null>(null);
const isDragging = ref(false);
const dragged = ref(false);
const startX = ref(0);
const scrollLeft = ref(0);
const activeIndex = ref(0);

const domScrollLeft = ref(0);
const domScrollWidth = ref(0);
const domClientWidth = ref(0);

function updateScrollMetrics() {
  if (!grid.value) return;
  domScrollLeft.value = grid.value.scrollLeft;
  domScrollWidth.value = grid.value.scrollWidth;
  domClientWidth.value = grid.value.clientWidth;
}

function todayIsoDate(): string {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');

  return `${yyyy}-${mm}-${dd}`;
}

function getStep(): number {
  if (!grid.value) return 0;

  const firstCard = grid.value.firstElementChild as HTMLElement | null;

  if (!firstCard) return 0;

  const gap = parseFloat(getComputedStyle(grid.value).columnGap || '0');

  return firstCard.offsetWidth + gap;
}

function updateActiveIndex() {
  if (!grid.value) return;

  const step = getStep();

  if (step <= 0) return;

  const idx = Math.round(grid.value.scrollLeft / step);

  activeIndex.value = idx;
  updateScrollMetrics();

  const date = props.events[idx]?.eventDate;

  if (date) emit('date-change', date);
}

watch(
  () => props.events,
  async (events) => {
    const today = todayIsoDate();
    const firstToday = events.findIndex((e) => e.eventDate === today);
    const target = firstToday === -1 ? 0 : firstToday;

    activeIndex.value = target;

    await nextTick();

    if (!grid.value) return;

    if (target === 0) {
      grid.value.scrollLeft = 0;
      updateScrollMetrics();

      return;
    }

    const step = getStep();

    if (step <= 0) return;

    grid.value.scrollLeft = step * target;
    updateScrollMetrics();
  },
  { immediate: true },
);

onMounted(async () => {
  await nextTick();
  updateScrollMetrics();
});

function goToSlide(index: number) {
  if (!grid.value) return;

  const step = getStep();

  if (step <= 0) return;

  grid.value.scrollTo({ left: step * index, behavior: 'smooth' });
}

function scrollToDate(date: string) {
  const idx = props.events.findIndex((e) => e.eventDate === date);

  if (idx === -1) return;

  goToSlide(idx);
}

defineExpose({
  activeIndex,
  goToSlide,
  scrollToDate,
  domScrollLeft,
  domScrollWidth,
  domClientWidth,
});

function onMouseDown(e: MouseEvent) {
  if (!grid.value) return;
  isDragging.value = true;
  startX.value = e.pageX - grid.value.offsetLeft;
  scrollLeft.value = grid.value.scrollLeft;
  dragged.value = false;
}

function onMouseMove(e: MouseEvent) {
  if (!isDragging.value || !grid.value) return;
  e.preventDefault();

  const x = e.pageX - grid.value.offsetLeft;
  const walk = x - startX.value;

  if (Math.abs(walk) > 5) {
    dragged.value = true;
  }
  grid.value.scrollLeft = scrollLeft.value - walk;
}

function onMouseUp() {
  isDragging.value = false;
  if (dragged.value) {
    setTimeout(() => {
      dragged.value = false;
    }, 50);
  }
}

function onCaptureClick(e: MouseEvent) {
  if (dragged.value) {
    e.stopPropagation();
    e.preventDefault();
  }
}
</script>

<template>
  <div
    ref="grid"
    :class="[$style.grid, dragged && $style.dragging]"
    @click.capture="onCaptureClick"
    @mousedown="onMouseDown"
    @mouseleave="onMouseUp"
    @mousemove="onMouseMove"
    @mouseup="onMouseUp"
    @scroll.passive="updateActiveIndex"
  >
    <SportsCuratedEventsEventCard
      v-for="event in events"
      :key="event.id"
      :event="event"
    />
  </div>
</template>

<style module lang="postcss">
.grid {
  @apply flex flex-row gap-4 overflow-x-auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;
  cursor: grab;
  user-select: none;
  -webkit-user-select: none;

  @media (max-width: 1023px) {
    margin-right: -16px;
  }

  &::-webkit-scrollbar {
    display: none;
  }

  &.dragging {
    cursor: grabbing;

    /* Bloqueia interação nos cards enquanto arrasta */
    & > * {
      pointer-events: none;
    }
  }
}
</style>
